/**
 * Folha Gap Estimation Service
 *
 * Detects current-year months where folha (payroll) has not yet been posted
 * to the DRE and estimates their value using historical seasonality from the
 * last 2 years.
 *
 * Why: The DRE has a recurring lag for folha postings. When Pontual looks at
 * Lucro Líquido in early April with only Jan+Feb folha posted, the number is
 * artificially high by roughly 2 months of missing payroll. This hook exposes
 * the gap so the CEO Big Numbers card can show a corrected "Lucro Ajustado".
 *
 * Method:
 *  1. Fetch 2 years of DRE history through the same tRPC query used by
 *     `useTrendAnalysis` (tRPC cache dedupes — no extra round trip).
 *  2. Build a per-year monthly aggregate of Folha using
 *     `buildMonthlyAggregates` (shared with the trend service).
 *  3. Derive a historical seasonality curve (2-year simple mean of
 *     normalized % of annual folha) — naturally captures the Nov/Dec 13º
 *     spike.
 *  4. Find the last month of the current year that has a non-zero posted
 *     folha value.
 *  5. Scale: `implied_annual = lastPostedFolha / historicalCurve[lastIdx]`
 *  6. For every missing month (from Jan up to and including the current
 *     month), estimate as `implied_annual * historicalCurve[missingIdx]`.
 *
 * Fallbacks:
 *  - No posted folha yet in the current year → no estimates, hook returns
 *    empty `missingMonths` and `totalEstimate = 0`.
 *  - No historical data (or the last-posted month has a zero historical
 *    share) → naive fallback: every missing month is estimated at the same
 *    value as `lastPostedFolha`.
 */

import { trpc } from '@/api/trpc'
import { useMemo } from 'react'
import { toNum } from './financial-analytics.service'
import {
  averageCurveAcrossYears,
  buildMonthlyAggregates,
  normalizeToPercentCurves,
} from './seasonality-utils'
import type { MonthRow } from './trend-analysis.service'

const YEARS_BACK = 2

const MONTH_NAMES_PT = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
]

export interface MissingFolhaMonth {
  /** 0-based month index (0 = January) */
  monthIdx: number
  /** Human label, e.g. "Março" */
  monthLabel: string
  /** Estimated folha value in R$ */
  estimate: number
  /** Always true — estimate is heuristic, surfaced as "Aproximado" in UI */
  isApproximate: boolean
}

export interface FolhaGapResult {
  missingMonths: MissingFolhaMonth[]
  /** Sum of all estimated missing-month values (R$) */
  totalEstimate: number
  isLoading: boolean
}

interface UseFolhaGapEstimateParams {
  /** Period end date (YYYY-MM-DD). Used as the "current point in the year". */
  endDate: string
  enabled?: boolean
}

/**
 * Hook: fetches 2 years of historical DRE data (shared cache with the trend
 * hook) and returns the list of missing folha months for the current year
 * along with a seasonality-aware estimate for each.
 */
export function useFolhaGapEstimate({
  endDate,
  enabled = true,
}: UseFolhaGapEstimateParams): FolhaGapResult {
  const currentYear = parseInt(endDate.substring(0, 4), 10) || new Date().getFullYear()
  const historicalStart = `${currentYear - YEARS_BACK}-01-01`

  const { data: rows = [], isLoading } = trpc.dre.getReport.useQuery(
    {
      dataInicial: new Date(historicalStart),
      dataFinal: new Date(endDate),
    },
    {
      enabled: enabled && !!endDate,
      retry: false,
      staleTime: 5 * 60 * 1000,
    }
  )

  return useMemo(() => {
    const empty: FolhaGapResult = {
      missingMonths: [],
      totalEstimate: 0,
      isLoading,
    }
    if (!rows.length) return empty

    // Current-month index (0-based) from endDate
    const d = new Date(endDate + 'T00:00:00')
    if (Number.isNaN(d.getTime())) return empty
    const currentMonthIdx = d.getMonth()

    // Aggregate folha per year/month
    const byYear = buildMonthlyAggregates(
      rows as MonthRow[],
      (r) => Number(r.Ano),
      (r) => Number(r.Mes),
      (r) => Math.abs(toNum(r.Folha)),
    )

    const currentYearMonths = byYear.get(currentYear) ?? new Array(12).fill(0)

    // Identify missing months in the current year (from Jan up to current month)
    const missingIdxs: number[] = []
    let lastPostedIdx = -1
    let lastPostedValue = 0
    for (let m = 0; m <= currentMonthIdx; m++) {
      const val = currentYearMonths[m]
      if (val > 0) {
        lastPostedIdx = m
        lastPostedValue = val
      } else {
        missingIdxs.push(m)
      }
    }

    if (missingIdxs.length === 0) {
      return { missingMonths: [], totalEstimate: 0, isLoading }
    }

    // No posted folha yet → we cannot estimate
    if (lastPostedIdx < 0 || lastPostedValue <= 0) {
      return { missingMonths: [], totalEstimate: 0, isLoading }
    }

    // Historical seasonality curve (last N years, excluding current year)
    const historicalYears = Array.from(byYear.keys())
      .filter((y) => y < currentYear)
      .sort((a, b) => a - b)

    const allCurves = normalizeToPercentCurves(byYear)
    const historicalCurves: Record<number, number[]> = {}
    for (const year of historicalYears) {
      historicalCurves[year] = allCurves[year]
    }
    const averageCurve = averageCurveAcrossYears(historicalCurves, historicalYears)

    const lastPostedShare = averageCurve[lastPostedIdx] ?? 0

    // Fallback: no historical curve → naive estimate (same value for every missing month)
    const useFallback = historicalYears.length === 0 || lastPostedShare <= 0

    const impliedAnnual = useFallback ? 0 : lastPostedValue / lastPostedShare

    const missingMonths: MissingFolhaMonth[] = missingIdxs.map((idx) => {
      const estimate = useFallback
        ? lastPostedValue
        : impliedAnnual * (averageCurve[idx] ?? 0)
      return {
        monthIdx: idx,
        monthLabel: MONTH_NAMES_PT[idx] ?? '',
        estimate,
        isApproximate: true,
      }
    })

    const totalEstimate = missingMonths.reduce((s, m) => s + m.estimate, 0)

    return { missingMonths, totalEstimate, isLoading }
  }, [rows, isLoading, endDate, currentYear])
}
